import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { CheckCircle, XCircle, Loader2, ArrowRight, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
            headers: getAuthHeaders(),
            withCredentials: true
          });
          
          setPaymentData(response.data);

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            return;
          } else if (response.data.status === 'expired') {
            setStatus('error');
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            // Max attempts reached, show result based on current status
            if (response.data.payment_status === 'paid') {
              setStatus('success');
            } else {
              setStatus('error');
            }
          }
        } catch (error) {
          console.error('Failed to check payment status:', error);
          setStatus('error');
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId, getAuthHeaders]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-20 h-20 mx-auto text-[#2D4A5E] animate-spin mb-6" />
              <h1 className="text-2xl lg:text-3xl font-bold text-[#1A202C] mb-4">
                Processing Your Payment
              </h1>
              <p className="text-[#64748B]">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#1A202C] mb-4" data-testid="success-title">
                Payment Successful!
              </h1>
              <p className="text-[#64748B] mb-8">
                Thank you for your purchase. Your content is now available in your dashboard.
              </p>
              
              {paymentData && (
                <div className="bg-[#F8F9FA] rounded-xl p-6 mb-8 text-left">
                  <h3 className="font-semibold text-[#1A202C] mb-3">Order Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Amount Paid</span>
                      <span className="font-medium">${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B]">Status</span>
                      <span className="text-green-600 font-medium">Completed</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dashboard">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full px-8 py-6 btn-hover-lift" data-testid="go-to-dashboard">
                    <Package className="w-5 h-5 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button variant="outline" className="rounded-full px-8 py-6">
                    Continue Shopping
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[#1A202C] mb-4">
                Payment Issue
              </h1>
              <p className="text-[#64748B] mb-8">
                There was an issue processing your payment. Please try again or contact support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/cart">
                  <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full px-8 py-6">
                    Return to Cart
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="rounded-full px-8 py-6">
                    Go Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
